// src/components/sales/sections/PerformanceFilters.jsx
import React from 'react';
import { Calendar, User, Filter, AlertCircle, RefreshCw, Clock, CalendarDays } from 'lucide-react';
import { useFilterOptions } from '../../../hooks/useFilterOptions';

const PerformanceFilters = ({ filters, onFiltersChange, loading, dataInfo }) => {
  const { availableClosers, availableOfferTypes, loading: optionsLoading, error: optionsError, refreshOptions } = useFilterOptions();

  const filterOptions = {
    aggregation_level: [
      { value:'weekly',  label:'Weekly (7-day rolling)',  description:'7-day rolling periods' },
      { value:'monthly', label:'Monthly (30-day rolling)', description:'30-day rolling periods' },
    ],
    periods_back: {
      weekly:  [{value:'all',label:'All Available Data'},{value:4,label:'Last 4 weeks'},{value:8,label:'Last 8 weeks'},{value:12,label:'Last 12 weeks'},{value:26,label:'Last 6 months'},{value:52,label:'Last year'}],
      monthly: [{value:'all',label:'All Available Data'},{value:3,label:'Last 3 months'},{value:6,label:'Last 6 months'},{value:12,label:'Last 12 months'},{value:24,label:'Last 2 years'}],
    },
    closer: [...new Set(availableClosers)].map((c,i) => ({ value:c, label: c==='all'?'All Team Members':c.charAt(0).toUpperCase()+c.slice(1), key:`closer-${c}-${i}` })),
    offer_type: [...new Set(availableOfferTypes.map(ot=>typeof ot==='string'?ot:ot.id))].map((ot,i) => {
      const orig = availableOfferTypes.find(o=>(typeof o==='string'?o:o.id)===ot);
      return { value: typeof orig==='string'?orig:orig.id, label: typeof orig==='string'?(orig==='all'?'All Offers':orig.toUpperCase()):orig.label, key:`ot-${ot}-${i}` };
    }),
  };

  const defaults = { aggregation_level:'weekly', reference_date:new Date().toISOString().split('T')[0], periods_back:12, closer:'all', offer_type:'all' };
  const cur = { ...defaults, ...filters };

  const handle = (key, val) => {
    const next = { ...cur };
    if (key==='aggregation_level') { next[key]=val; next.periods_back=val==='weekly'?12:6; next.closer='all'; }
    else if (key==='periods_back') next[key]=val==='all'?'all':parseInt(val);
    else next[key]=val;
    onFiltersChange?.(next);
  };

  const hasActive = cur.aggregation_level!=='weekly'||cur.reference_date!==defaults.reference_date||cur.periods_back!==12||cur.closer!=='all'||cur.offer_type!=='all';
  const periodsOpts = filterOptions.periods_back[cur.aggregation_level]||[];

  const getDesc = () => {
    const ref = new Date(cur.reference_date);
    if (cur.periods_back==='all') return `All available data up to ${ref.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'2-digit'})}`;
    const start = new Date(ref - cur.periods_back*(cur.aggregation_level==='weekly'?7:30)*864e5);
    const fmt = d => new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'2-digit'});
    return `${cur.periods_back} ${cur.aggregation_level} periods from ${fmt(cur.reference_date)} back to ${fmt(start.toISOString().split('T')[0])}`;
  };

  const inputCls = 'w-full p-2 border border-grey-300 rounded-lg text-xs bg-white focus:ring-2 focus:ring-brightblue-300 disabled:bg-grey-100 disabled:text-grey-400';

  if (loading && availableClosers.length<=1) return (
    <div className="bg-grey-50 border-b border-grey-200 p-4 mb-4 flex items-center justify-center gap-2">
      <RefreshCw className="h-4 w-4 animate-spin text-grey-400" /><span className="text-xs text-grey-500">Loading filter options…</span>
    </div>
  );

  if (optionsError) return (
    <div className="bg-pink-50 border border-pink-200 p-4 mb-4 rounded-lg flex items-center text-pink-700">
      <AlertCircle className="h-4 w-4 mr-2" />
      <span className="text-xs font-medium">Error loading options: {optionsError}</span>
      <button onClick={refreshOptions} className="ml-auto text-xs text-pink-600 hover:text-pink-800 underline">Retry</button>
    </div>
  );

  return (
    <div className="bg-grey-50 border-b border-grey-200 p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div />
        <div className="flex items-center gap-2">
          {hasActive && <button onClick={()=>onFiltersChange?.(defaults)} disabled={loading||optionsLoading} className="text-xs text-brightblue-600 hover:text-brightblue-700 underline">Clear Filters</button>}
          {loading && <span className="flex items-center gap-1 text-xs text-grey-400"><RefreshCw className="h-3.5 w-3.5 animate-spin" />Updating…</span>}
        </div>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Reference date */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-grey-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-grey-500 mb-1">Reference Date</label>
              <input type="date" value={cur.reference_date} onChange={e=>handle('reference_date',e.target.value)} disabled={loading||optionsLoading} className={inputCls} />
            </div>
          </div>
          {/* Aggregation level */}
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-grey-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-grey-500 mb-1">Aggregation Level</label>
              <select value={cur.aggregation_level} onChange={e=>handle('aggregation_level',e.target.value)} disabled={loading||optionsLoading} className={inputCls}>
                {filterOptions.aggregation_level.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {/* Periods */}
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-grey-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-grey-500 mb-1">Number of Periods</label>
              <select value={cur.periods_back} onChange={e=>handle('periods_back',e.target.value)} disabled={loading||optionsLoading} className={inputCls}>
                {periodsOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {/* Team member */}
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-grey-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <label className="block text-xs text-grey-500 mb-1">
                Team Member {optionsLoading&&<span className="text-grey-300 ml-1">(loading…)</span>}
              </label>
              <select value={cur.closer} onChange={e=>handle('closer',e.target.value)} disabled={loading||optionsLoading} className={inputCls}>
                {filterOptions.closer.map(o=><option key={o.key||o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 text-xs text-grey-400">
          <div>Aggregated backwards from this date</div>
          <div>{filterOptions.aggregation_level.find(o=>o.value===cur.aggregation_level)?.description}</div>
          <div>Rolling periods to include</div>
          <div>Members with data in period</div>
        </div>
      </div>

      {hasActive && (
        <div className="mt-3 p-3 bg-brightblue-50 border border-brightblue-200 rounded-lg">
          <div className="flex items-start text-xs text-brightblue-700">
            <Filter className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="flex flex-wrap gap-1.5 mb-1">
                <span className="font-semibold">Showing:</span>
                <span className="bg-brightblue-100 px-2 py-0.5 rounded">{periodsOpts.find(o=>o.value===cur.periods_back)?.label||`${cur.periods_back} periods`}</span>
                <span className="bg-brightblue-100 px-2 py-0.5 rounded">{filterOptions.aggregation_level.find(o=>o.value===cur.aggregation_level)?.label}</span>
                {cur.closer!=='all'&&<span className="bg-brightblue-100 px-2 py-0.5 rounded">{filterOptions.closer.find(o=>o.value===cur.closer)?.label}</span>}
              </div>
              <p className="text-brightblue-600">{getDesc()}</p>
            </div>
            {(loading||optionsLoading)&&<span className="ml-auto flex items-center gap-1 flex-shrink-0"><RefreshCw className="h-3 w-3 animate-spin" />{optionsLoading?'Loading…':'Updating…'}</span>}
          </div>
        </div>
      )}

      {!loading&&!optionsLoading&&availableClosers.length<=1&&(
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
          <span className="text-xs text-yellow-700">Limited filter options — check connectivity or data availability.</span>
        </div>
      )}
    </div>
  );
};

export default PerformanceFilters;